'use client';

import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Check, Hexagon, Layers } from 'lucide-react';
import type { MapStyleKey } from './droneMapTypes';
import { MAP_LAYERS_MENU } from '@/lib/copy';

export interface MapLayersMenuProps {
  airspace: boolean;
  missions: boolean;
  altitudeLabels: boolean;
  mapStyle: MapStyleKey;
  onToggleAirspace: () => void;
  onToggleMissions: () => void;
  onToggleAltitudeLabels: () => void;
  /**
   * Picks a basemap by name. This used to be a no-argument `onToggleStyle` that
   * CYCLED, which worked only while there were exactly two: with a third, "the
   * other one" stops being a well-defined destination and every radio item has
   * to say which one it means.
   */
  onSelectStyle: (key: MapStyleKey) => void;
  /**
   * Whether this deployment can serve imagery at all. Satellite comes from our
   * own API rather than a CDN, and a server with no GOOGLE_MAPS_API_KEY answers
   * 501 - so the item is hidden rather than offered and then blanking the map.
   */
  satelliteAvailable: boolean;
  /**
   * Opens the zones sheet. Absent when the operator cannot author zones, and
   * then no item is drawn.
   */
  onOpenZones?: () => void;
}

const ITEM_CLASS =
  'flex w-full items-center gap-2 px-3 py-2 text-left text-muted-foreground row-hover hover:text-foreground transition-colors';

/** Gap between the trigger's left edge and the menu's right edge, in px. */
const MENU_GAP_PX = 4;

/**
 * The four view toggles that were four keys in the map's control column.
 *
 * Slice 6 of the declutter plan, as amended on 2026-09-04: My location stays a
 * button (amendment A, it is a navigation action and burying a recentre behind a
 * menu is how an operator stops using it) and range rings went to the aircraft's
 * right-click menu (amendment B), so what is left in here is airspace, mission
 * routes, altitude labels and the basemap - the four that are set once a shift
 * rather than during a sortie. Nine persistent keys at 44 px plus eight gaps was
 * 428 px of the map's right edge; three plus a conditional north is 140 px.
 *
 * Every item dispatches the SAME handler its key did, so what is persisted and
 * what the map draws are unchanged. This moves controls, it does not redefine
 * them.
 */
export function MapLayersMenu({
  airspace,
  missions,
  altitudeLabels,
  mapStyle,
  onToggleAirspace,
  onToggleMissions,
  onToggleAltitudeLabels,
  onSelectStyle,
  satelliteAvailable,
  onOpenZones,
}: MapLayersMenuProps) {
  /**
   * Where to draw the menu, or null while it is shut.
   *
   * Measured from the trigger rather than positioned against it, because the
   * menu is PORTALLED. The control column it lives in is
   * `max-h-[...] overflow-y-auto`, and a box with `overflow-y` other than
   * visible computes its `overflow-x` to `auto` as well - so the column is a
   * scroll container on both axes, and a child placed at `right: 100%` sits at
   * a negative offset inside it. That is clipped and it is not scrollable to,
   * because `scrollLeft` cannot go below zero: the menu would simply never
   * appear. jsdom has no layout, so no unit test here can see that.
   */
  const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(null);
  const open = anchor !== null;
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => setAnchor(null), []);

  const openMenu = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Anchored by its RIGHT edge to the trigger's left edge, so the menu grows
    // leftward over the map and never off the viewport - this column is against
    // the map's right edge.
    setAnchor({ top: rect.top, right: window.innerWidth - rect.left + MENU_GAP_PX });
  }, []);

  // Outside press closes. `pointerdown` rather than `click`, because the surface
  // underneath is a live MapLibre canvas: a drag that starts outside the menu is
  // a pan, and the menu has to be gone by the time the map has moved, not one
  // mouseup later. Listening does not consume the event, so the pan itself still
  // happens.
  //
  // Both boxes are asked, because the panel is not a DOM descendant of the root
  // any more - only a React one.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, close]);

  // A fixed box measured once cannot follow the trigger, and the trigger moves
  // whenever the side panel opens or the window is resized. Closing is honest;
  // re-anchoring on every frame of a drag is not worth it for a menu that is
  // open for a few seconds.
  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', close);
    // Capture, because the thing that scrolls is the control column itself and a
    // scroll event does not bubble. On a short viewport with a drone selected
    // that column really does scroll, and a wheel over it fires neither a
    // pointerdown nor a resize - the menu would sit still while its key slid out
    // from under it.
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [open, close]);

  /**
   * Move focus onto a menu item, wrapping at both ends.
   *
   * `role="menu"` promises this, and the portal makes it load-bearing rather
   * than merely correct: the items are appended to `document.body`, so a
   * keyboard operator who tabbed out of the trigger would land on Fullscreen
   * and find the items after every other focusable thing on the page.
   */
  const focusItem = useCallback((index: number) => {
    const items = panelRef.current?.querySelectorAll<HTMLButtonElement>('[role^="menuitem"]');
    if (!items?.length) return;
    const wrapped = (index + items.length) % items.length;
    items[wrapped].focus();
  }, []);

  // On open, not in a layout effect: the panel has to be in the DOM first.
  useEffect(() => {
    if (open) focusItem(0);
  }, [open, focusItem]);

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (!open) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      e.stopPropagation();
      const items = Array.from(
        panelRef.current?.querySelectorAll<HTMLButtonElement>('[role^="menuitem"]') ?? [],
      );
      const at = items.indexOf(document.activeElement as HTMLButtonElement);
      if (e.key === 'Home') focusItem(0);
      else if (e.key === 'End') focusItem(items.length - 1);
      else focusItem(at + (e.key === 'ArrowDown' ? 1 : -1));
      return;
    }
    // Scoped to this element, not to the window: the map's context menu keeps
    // its own Escape, and an armed GO-TO keeps its own. Only the topmost open
    // thing should answer. This still catches Escape pressed inside the menu:
    // React propagates events through the component tree, not the DOM one, so a
    // portalled child bubbles to its JSX parent.
    e.stopPropagation();
    close();
    triggerRef.current?.focus();
  }

  return (
    <div ref={rootRef} className="relative" onKeyDown={handleKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        title={MAP_LAYERS_MENU.trigger}
        aria-label={MAP_LAYERS_MENU.trigger}
        onClick={() => (open ? close() : openMenu())}
        className={
          open
            ? 'flex items-center justify-center size-11 rounded-md bg-muted border border-input text-foreground'
            : 'flex items-center justify-center size-11 rounded-md bg-popover/90 border border-subtle text-muted-foreground hover:text-foreground transition-colors'
        }
      >
        <Layers className="size-5" strokeWidth={1.5} />
      </button>

      {anchor && createPortal(
        <div
          ref={panelRef}
          id={menuId}
          role="menu"
          aria-label={MAP_LAYERS_MENU.trigger}
          data-testid="map-layers-menu"
          // `fixed`, and in a portal, to escape the control column's scroll
          // container (see `anchor` above). Anchored by its right edge to the
          // trigger's left edge and by its top to the trigger's top, so it opens
          // leftward over the map and never reaches up over the strip board.
          style={{ position: 'fixed', top: anchor.top, right: anchor.right }}
          // `bg-popover/95`, NOT `glass-card`. `--glass-bg` is rgba(255,255,255,.05),
          // so a glass ground here is a blurred picture of whatever basemap is
          // underneath - and on the street basemap in daylight the items are
          // grey on near-white. Every key this menu replaces used `bg-popover/90`
          // and `MapLegend`'s own panel does too.
          className="z-30 min-w-[190px] rounded-lg border border-subtle bg-popover/95 backdrop-blur-sm py-1 text-sm shadow-lg"
        >
          <Toggle
            label={MAP_LAYERS_MENU.airspace}
            hint={MAP_LAYERS_MENU.airspaceHint}
            checked={airspace}
            onSelect={onToggleAirspace}
          />
          <Toggle
            label={MAP_LAYERS_MENU.missionRoutes}
            checked={missions}
            onSelect={onToggleMissions}
          />
          <Toggle
            label={MAP_LAYERS_MENU.altitudeLabels}
            checked={altitudeLabels}
            onSelect={onToggleAltitudeLabels}
          />

          <div
            role="group"
            aria-label={MAP_LAYERS_MENU.basemapGroup}
            className="mt-1 border-t divider pt-1"
          >
            <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-muted-foreground">
              {MAP_LAYERS_MENU.basemapGroup}
            </div>
            <Choice
              label={MAP_LAYERS_MENU.basemapDark}
              checked={mapStyle === 'dark'}
              onSelect={() => onSelectStyle('dark')}
            />
            <Choice
              label={MAP_LAYERS_MENU.basemapStreet}
              checked={mapStyle === 'bright'}
              onSelect={() => onSelectStyle('bright')}
            />
            {satelliteAvailable && (
              <Choice
                label={MAP_LAYERS_MENU.basemapSatellite}
                checked={mapStyle === 'satellite'}
                onSelect={() => onSelectStyle('satellite')}
              />
            )}
          </div>

          {onOpenZones && (
            /* An ACTION, not a view toggle, so it sits under its own divider and
               closes the menu. It is here because the zones sheet is otherwise
               unreachable from an idle COP: slice 8 took the Zones key off the
               map, and the right column's tab strip only exists once some other
               sheet has been opened. */
            <div className="mt-1 border-t divider pt-1">
              <button
                type="button"
                role="menuitem"
                title={MAP_LAYERS_MENU.openZonesHint}
                onClick={() => {
                  close();
                  onOpenZones();
                }}
                className={ITEM_CLASS}
              >
                <span aria-hidden className="flex size-4 shrink-0 items-center justify-center">
                  <Hexagon className="size-3.5" strokeWidth={1.5} />
                </span>
                <span className="truncate">{MAP_LAYERS_MENU.openZones}</span>
              </button>
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

/**
 * A view layer, on or off.
 *
 * The menu deliberately stays OPEN on a toggle. T6's interaction spec says a
 * menu closes "on Escape, outside click, or a choice", which is right for a menu
 * of commands and wrong for a menu of checkboxes: an operator setting up a
 * picture turns on two layers, and closing after each one makes that four
 * presses instead of three. It matches what a `menuitemcheckbox` does elsewhere,
 * and Escape and an outside press both still close it.
 */
function Toggle({
  label,
  hint,
  checked,
  onSelect,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={checked}
      title={hint}
      onClick={onSelect}
      className={ITEM_CLASS}
    >
      <CheckSlot checked={checked} />
      <span className="truncate">{label}</span>
    </button>
  );
}

/** One of the two basemaps. Picking the one already drawn does nothing. */
function Choice({
  label,
  checked,
  onSelect,
}: {
  label: string;
  checked: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={checked}
      // Fires even when this basemap is the one already drawn. Picking the
      // current basemap changes nothing on screen and everything in the record:
      // it is how an operator says "this one, keep it", and it is the only way
      // they can say it. Refusing it meant an operator on the deployment's
      // default could not opt out of a later default change.
      onClick={onSelect}
      className={ITEM_CLASS}
    >
      <CheckSlot checked={checked} />
      <span className="truncate">{label}</span>
    </button>
  );
}

/**
 * Fixed 16 px whether or not the tick is drawn, so the labels form one column
 * and the rows do not shift sideways as layers go on and off under the cursor.
 */
function CheckSlot({ checked }: { checked: boolean }) {
  return (
    <span aria-hidden className="flex size-4 shrink-0 items-center justify-center">
      {checked && <Check className="size-4 text-status-blue-fg" strokeWidth={2} />}
    </span>
  );
}
